// components/UserProfile/UserProfileManager.js
import React, { useState, useEffect, useCallback } from 'react';
import { Snackbar, Alert } from '@mui/material';
import UserProfileButton from './UserProfileButton';
import UserDropdown from './UserDropdown';
import EditUserProfile from './EditUserProfile';
import PhotoGallery from './PhotoGallery';
import WelcomeBanner from '../WelcomeBanner';
import UserProfileServices from '../../services/userProfile.services';
import jwt_decode from 'jwt-decode';
import { getJwtToken } from '../../services/jwt.services';
import { getInitialsFromEmail, getDisplayNameFromEmail } from '../../utils/userUtils';
import logger from '../../utils/logger';

const UserProfileManager = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [username, setUsername] = useState('');
  const [userGroups, setUserGroups] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [preferredName, setPreferredName] = useState('');
  const [birthday, setBirthday] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [timezone, setTimezone] = useState('');
  const [city, setCity] = useState('');
  const [originalProfile, setOriginalProfile] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [photoGalleryOpen, setPhotoGalleryOpen] = useState(false);
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(false);
  
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotification = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
  
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // PhotoGallery handlers
  const handleClosePhotoGallery = () => {
    setPhotoGalleryOpen(false);
    // Refresh user profile when gallery closes to ensure data is in sync
    fetchUserProfile(true); // Force refresh to get latest data
  };

  const handlePhotoSelected = (newPhotoUrl) => {
    setProfilePicture(newPhotoUrl);
    // Refresh user profile to get updated profile picture
    fetchUserProfile();
  };
  
  const handleOpenProfileDialog = () => {
    if (userProfile) {
      setOriginalProfile({
        preferredName: userProfile.preferredName || '',
        birthday: userProfile.birthday || '',
        statusMessage: userProfile.statusMessage || '',
        timezone: userProfile.timezone || '',
        city: userProfile.city || '',
      });
      
      setPreferredName(userProfile.preferredName || '');
      setBirthday(userProfile.birthday || '');
      setStatusMessage(userProfile.statusMessage || '');
      setTimezone(userProfile.timezone || '');
      setCity(userProfile.city || '');
    }
    setOpenProfileDialog(true);
    handleMenuClose();
  };
  
  const handleCloseProfileDialog = () => {
    setOpenProfileDialog(false);
  };

  const handleProfilePictureClick = () => {
    setPhotoGalleryOpen(true);
  };
  
  const handleFileChange = async (event) => {
    
    const file = event.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      logger.error('File too large: maximum size is 5MB');
      handleNotification('File too large: maximum size is 5MB', 'error');
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      logger.error('Invalid file type: only images are allowed');
      handleNotification('Invalid file type: only images are allowed', 'error');
      return;
    }
    
    const tempUrl = URL.createObjectURL(file);
    setProfilePicture(tempUrl);
    
    const uploadResponse = await UserProfileServices.uploadProfilePicture(file);
    
    if (uploadResponse.success) {
      const profileData = await UserProfileServices.getCurrentUser();
      if (profileData && profileData.profilePictureUrl) {
        setProfilePicture(profileData.profilePictureUrl);
      }
      
      URL.revokeObjectURL(tempUrl);
      handleNotification(uploadResponse.message, 'success');
    } else {
      logger.error('Error uploading profile picture:', uploadResponse.error);
      
      if (userProfile && userProfile.profilePictureUrl) {
        setProfilePicture(userProfile.profilePictureUrl);
      } else {
        setProfilePicture(null);
      }
      
      URL.revokeObjectURL(tempUrl);
      handleNotification(uploadResponse.message, 'error');
    }
  };
  
  const handleUpdateProfile = async (profileUpdateData, isRefreshOnly = false) => {
    
    try {
      // If this is just a refresh call, don't process any updates
      if (isRefreshOnly) {
        const updatedProfile = await UserProfileServices.getCurrentUser();
        setUserProfile(updatedProfile);
        return;
      }
      
      // The EditUserProfile component now handles birthday restrictions
      const updateData = profileUpdateData || {
        preferredName: preferredName || '',
        birthday: birthday || '',
        statusMessage: statusMessage || '',
        timezone: timezone || '',
        city: city || ''
      };
      
      if (!profileUpdateData) {
        Object.keys(updateData).forEach(key => {
          if (originalProfile && originalProfile[key] === updateData[key]) {
            delete updateData[key];
          }
        });
      }
      
      if (Object.keys(updateData).length > 0) {
        try {
          const response = await UserProfileServices.updateUserProfile(updateData);
          
          // Check if the backend returned a validation error (success: false)
          if (response.data && response.data.success === false) {
            // Handle validation failures
            const errorMessage = response.data.message || 'Profile update failed due to validation errors';
            handleNotification(errorMessage, 'error');
            
            // Show specific restriction messages if available
            if (response.data.restrictionMessages && response.data.restrictionMessages.length > 0) {
              setTimeout(() => {
                response.data.restrictionMessages.forEach((restrictionMessage, index) => {
                  setTimeout(() => {
                    handleNotification(restrictionMessage, 'error');
                  }, index * 300);
                });
              }, 300);
            }
            
            // Don't close the modal or update profile data on validation failure
            return;
          }
          
          // Fetch fresh profile data to update the UI immediately (only for successful updates)
          const updatedProfile = await UserProfileServices.getCurrentUser();
          setUserProfile(updatedProfile);
          
          // Update all related state with fresh data immediately
          if (updatedProfile) {
            if (updatedProfile.preferredName) {
              setUserName(updatedProfile.preferredName);
            }
            if (updatedProfile.profilePictureUrl) {
              setProfilePicture(updatedProfile.profilePictureUrl);
            }
            if (updatedProfile.groups && Array.isArray(updatedProfile.groups)) {
              setUserGroups(updatedProfile.groups);
            }
          }
          
          // Refresh the page in the background to ensure all components are updated
          setTimeout(() => {
            window.location.reload();
          }, 2000); // Give time for notifications to be seen
          
          if (!profileUpdateData && originalProfile) {
            setOriginalProfile({
              ...originalProfile,
              ...updateData
            });
          }
          
          const mainMessage = response.data.message || 'Profile updated successfully';
          
          handleNotification(mainMessage, 'success');
          
          if (response.data.restrictionMessages && response.data.restrictionMessages.length > 0) {
            // Add a slight delay for better user experience with multiple notifications
            setTimeout(() => {
              response.data.restrictionMessages.forEach((restrictionMessage, index) => {
                setTimeout(() => {
                  handleNotification(restrictionMessage, 'warning');
                }, index * 300);
              });
            }, 300);
          }
        } catch (error) {
          logger.error('Error updating profile:', error);
          const errorMessage = error.response?.data?.message || 'Failed to update profile. Please try again.';
          handleNotification(errorMessage, 'error');
        }
      } else if (!isRefreshOnly) {
        // Only show "no changes" message if this was an actual save attempt, not a refresh
        handleNotification('No changes were made to your profile', 'info');
      }
      
      // Don't close modal anymore - keep it open for immediate updates
    } catch (error) {
      logger.error('Error in profile update process:', error);
      handleNotification('An unexpected error occurred', 'error');
      // Don't close modal on error anymore
    }
  };

  const fetchUserProfile = useCallback(async (forceRefresh = false) => {
    // Skip fetching if we already have profile data and not forcing refresh
    if (!forceRefresh && userProfile && Object.keys(userProfile).length > 0) {
      logger.debug('Using existing profile data, skipping API call');
      return;
    }
    
    try {
      const profileData = await UserProfileServices.getCurrentUser();
      setUserProfile(profileData);
      
      // Only show welcome banner if user just logged in
      const justLoggedIn = sessionStorage.getItem('justLoggedIn');
      if (justLoggedIn === 'true') {
        setShowWelcomeBanner(true);
        // Clear the flag so banner doesn't show again during this session
        sessionStorage.removeItem('justLoggedIn');
      }
      
      if (profileData && profileData.groups && Array.isArray(profileData.groups)) {
        setUserGroups(profileData.groups);
      } else {
        setUserGroups([]);
        logger.warn('No groups found in profile data or invalid format');
      }
      
      if (profileData && profileData.profilePictureUrl) {
        setProfilePicture(profileData.profilePictureUrl);
      }
      if (profileData && profileData.preferredName) {
        setUserName(profileData.preferredName);
      }
      if (profileData && profileData.username) {
        setUsername(profileData.username);
        if (!userEmail && profileData.username) {
          setUserEmail(profileData.username);
        }
      }
    } catch (error) {
      logger.error('Error fetching user profile:', error);
    }
  }, [userEmail, userProfile]);
  
  // Function to refresh profile data for EditUserProfile component
  const handleRefreshProfile = useCallback(() => {
    logger.debug('Refreshing profile data from EditUserProfile component');
    fetchUserProfile(true); // Force refresh
  }, [fetchUserProfile]);

  useEffect(() => {
    const jwtToken = getJwtToken();
    if (jwtToken) {
      try {
        const decoded = jwt_decode(jwtToken);
        const email = decoded.email || '';
        
        const name = decoded.name || getDisplayNameFromEmail(email);
        
        setUserName(name);
        setUserEmail(email);
        
        // We'll determine returning status in fetchUserProfile based on lastLogin field
        fetchUserProfile();
      } catch (error) {
        logger.error('Error decoding token:', error);
      }
    }
  }, [fetchUserProfile]);
  
  const userInitial = userName && userName !== 'User' ? userName.charAt(0).toUpperCase() : (userEmail ? getInitialsFromEmail(userEmail) : 'U');
  
  return (
    <div>
      {showWelcomeBanner && (
        <WelcomeBanner 
          userName={userName}
          userEmail={userEmail}
          showBanner={showWelcomeBanner}
        />
      )}
      <UserProfileButton 
        profilePicture={profilePicture}
        userName={userName}
        userEmail={userEmail}
        onClick={handleMenuOpen}
        onFileChange={handleFileChange}
      />
      
      <UserDropdown
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        userProfile={userProfile}
        userEmail={userEmail}
        userName={userName}
        userInitial={userInitial}
        profilePicture={profilePicture}
        userGroups={userGroups}
        onEditProfile={handleOpenProfileDialog}
        onLogout={handleMenuClose}
        onProfilePictureClick={handleProfilePictureClick}
      />
      
      <EditUserProfile 
        open={openProfileDialog}
        onClose={handleCloseProfileDialog}
        userProfile={userProfile}
        onSave={handleUpdateProfile}
        userInitial={userInitial}
        userName={userName}
        profilePicture={profilePicture}
        setProfilePicture={setProfilePicture}
        onNotification={handleNotification}
        onRefreshProfile={handleRefreshProfile}
      />
      
      <PhotoGallery
        open={photoGalleryOpen}
        onClose={handleClosePhotoGallery}
        onPhotoSelected={handlePhotoSelected}
        onNotification={handleNotification}
        userProfile={userProfile}
      />
      
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }} 
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default UserProfileManager;
